/**
 * TeardownPage — /early-career/teardown
 *
 * The free "portfolio teardown" lead-magnet promised by the email-capture
 * fallback on /early-career. Public route but deliberately UNLISTED on the
 * landing page itself — subscribers receive the link on form submit (instant
 * delivery in EarlyCareerHome's success state) and via the Formspree
 * autoresponse. A static PDF twin lives at /teardown.pdf.
 *
 * Same conventions as EarlyCareerHome: colours entirely from the MUI theme
 * palette (dark-mode safe), typography on the native 400/500 scale with the
 * 700 eyebrow exception, no localStorage/sessionStorage, honesty/legal copy
 * preserved (no employment guarantees).
 */

import {
  Box,
  Chip,
  Container,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Content — factories so copy can be translated; called inside the
// component, which has a translation hook.
// ---------------------------------------------------------------------------

function getStructureLines(t: TFunction): string[] {
  return [
    t("TeardownPage.structureLine1"),
    t("TeardownPage.structureLine2"),
    t("TeardownPage.structureLine3"),
    t("TeardownPage.structureLine4"),
    t("TeardownPage.structureLine5"),
  ];
}

interface TeardownPiece {
  eyebrow: string;
  title: string;
  test: string;
  body: string[];
  kills: string;
  passes: string;
}

function getPieces(t: TFunction): TeardownPiece[] {
  return [
    {
      eyebrow: t("TeardownPage.piece1Eyebrow"),
      title: t("TeardownPage.piece1Title"),
      test: t("TeardownPage.piece1Test"),
      body: [t("TeardownPage.piece1Body1"), t("TeardownPage.piece1Body2")],
      kills: t("TeardownPage.piece1Kills"),
      passes: t("TeardownPage.piece1Passes"),
    },
    {
      eyebrow: t("TeardownPage.piece2Eyebrow"),
      title: t("TeardownPage.piece2Title"),
      test: t("TeardownPage.piece2Test"),
      body: [t("TeardownPage.piece2Body1"), t("TeardownPage.piece2Body2")],
      kills: t("TeardownPage.piece2Kills"),
      passes: t("TeardownPage.piece2Passes"),
    },
    {
      eyebrow: t("TeardownPage.piece3Eyebrow"),
      title: t("TeardownPage.piece3Title"),
      test: t("TeardownPage.piece3Test"),
      body: [t("TeardownPage.piece3Body1")],
      kills: t("TeardownPage.piece3Kills"),
      passes: t("TeardownPage.piece3Passes"),
    },
    {
      eyebrow: t("TeardownPage.piece4Eyebrow"),
      title: t("TeardownPage.piece4Title"),
      test: t("TeardownPage.piece4Test"),
      body: [t("TeardownPage.piece4Body1"), t("TeardownPage.piece4Body2")],
      kills: t("TeardownPage.piece4Kills"),
      passes: t("TeardownPage.piece4Passes"),
    },
    {
      eyebrow: t("TeardownPage.piece5Eyebrow"),
      title: t("TeardownPage.piece5Title"),
      test: t("TeardownPage.piece5Test"),
      body: [t("TeardownPage.piece5Body1")],
      kills: t("TeardownPage.piece5Kills"),
      passes: t("TeardownPage.piece5Passes"),
    },
  ];
}

function getSelfTest(t: TFunction): string[] {
  return [
    t("TeardownPage.selfTest1"),
    t("TeardownPage.selfTest2"),
    t("TeardownPage.selfTest3"),
    t("TeardownPage.selfTest4"),
    t("TeardownPage.selfTest5"),
    t("TeardownPage.selfTest6"),
    t("TeardownPage.selfTest7"),
  ];
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function Eyebrow({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Typography
      variant="overline"
      fontWeight={700}
      sx={{ color: theme.palette.primary.main, letterSpacing: "0.14em", display: "block" }}
    >
      {children}
    </Typography>
  );
}

function PieceSection({ piece, index }: { piece: TeardownPiece; index: number }) {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  return (
    <Box sx={{ py: { xs: 3, md: 4 }, borderTop: index === 0 ? "none" : `1px solid ${theme.palette.divider}` }}>
      <Eyebrow>{piece.eyebrow}</Eyebrow>
      <Typography variant="h5" fontWeight={500} sx={{ mt: 0.5, mb: 1.5, letterSpacing: "-0.01em" }}>
        {piece.title}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          px: 2.5,
          py: 1.75,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Typography variant="body2" fontWeight={500}>
          {t("TeardownPage.testPrefix")} {piece.test}
        </Typography>
      </Paper>
      {piece.body.map((p) => (
        <Typography key={p.slice(0, 24)} variant="body1" color="text.secondary" sx={{ mb: 1.75, lineHeight: 1.7 }}>
          {p}
        </Typography>
      ))}
      <Stack spacing={1} sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          <Box component="span" sx={{ color: theme.palette.error.main, fontWeight: 700, mr: 1 }}>{t("TeardownPage.killsItLabel")}</Box>
          <Box component="span" sx={{ color: "text.secondary" }}>{piece.kills}</Box>
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          <Box component="span" sx={{ color: theme.palette.success.dark, fontWeight: 700, mr: 1 }}>{t("TeardownPage.passesLabel")}</Box>
          <Box component="span" sx={{ color: "text.secondary" }}>{piece.passes}</Box>
        </Typography>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function TeardownPage() {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const STRUCTURE_LINES = getStructureLines(t);
  const PIECES = getPieces(t);
  const SELF_TEST = getSelfTest(t);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="md">
          <Eyebrow>{t("TeardownPage.heroEyebrow")}</Eyebrow>
          <Typography
            variant="h3"
            fontWeight={500}
            sx={{ letterSpacing: "-0.02em", mt: 1, mb: 2, fontSize: { xs: "1.9rem", md: "2.5rem" }, lineHeight: 1.15 }}
          >
            {t("TeardownPage.heroHeading")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.7 }}>
            {t("TeardownPage.heroBody")}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              px: 2.5,
              py: 2,
              maxWidth: 640,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
              <Trans
                i18nKey="TeardownPage.honestyNote"
                t={t}
                components={[<strong key="b0" />, <em key="b1" />]}
              />
            </Typography>
          </Paper>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* The six-second reality */}
        <Eyebrow>{t("TeardownPage.sixSecondEyebrow")}</Eyebrow>
        <Typography variant="h5" fontWeight={500} sx={{ mt: 0.5, mb: 1.5 }}>
          {t("TeardownPage.sixSecondHeading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1.75, lineHeight: 1.7 }}>
          <Trans
            i18nKey="TeardownPage.sixSecondBody"
            t={t}
            components={[<em key="b0" />, <strong key="b1" />]}
          />
        </Typography>

        {/* Structure at a glance */}
        <Box sx={{ my: 4 }}>
          <Eyebrow>{t("TeardownPage.structureEyebrow")}</Eyebrow>
          <Paper
            elevation={0}
            sx={{
              mt: 1.5,
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              overflowX: "auto",
            }}
          >
            {STRUCTURE_LINES.map((line) => (
              <Typography
                key={line}
                component="pre"
                sx={{ fontFamily: "monospace", fontSize: "0.85rem", lineHeight: 1.8, m: 0, whiteSpace: "pre" }}
              >
                {line}
              </Typography>
            ))}
          </Paper>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.7 }}>
            {t("TeardownPage.structureFollowUp")}
          </Typography>
        </Box>

        {/* The five pieces */}
        {PIECES.map((piece, i) => (
          <PieceSection key={piece.title} piece={piece} index={i} />
        ))}

        {/* Self-test */}
        <Box sx={{ mt: 5 }}>
          <Eyebrow>{t("TeardownPage.selfTestEyebrow")}</Eyebrow>
          <Typography variant="h5" fontWeight={500} sx={{ mt: 0.5, mb: 2 }}>
            {t("TeardownPage.selfTestHeading")}
          </Typography>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: "hidden" }}>
            {SELF_TEST.map((item, i) => (
              <Stack
                key={item}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{ px: 2.5, py: 1.5, borderTop: i === 0 ? "none" : `1px solid ${theme.palette.divider}` }}
              >
                <CheckCircleIcon sx={{ fontSize: 18, color: alpha(theme.palette.text.primary, 0.3), mt: 0.3 }} />
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{item}</Typography>
              </Stack>
            ))}
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.65 }}>
            <Trans
              i18nKey="TeardownPage.selfTestSummary"
              t={t}
              components={[<strong key="b0" />, <strong key="b1" />, <strong key="b2" />]}
            />
          </Typography>
        </Box>

        {/* Honest close */}
        <Paper
          elevation={0}
          sx={{
            mt: 5,
            px: { xs: 2.5, md: 3.5 },
            py: 3,
            borderRadius: "0 12px 12px 0",
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <Trans
              i18nKey="TeardownPage.honestClose"
              t={t}
              components={[<strong key="b0" />]}
            />
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mt: 2 }}>
            <Chip
              label={t("TeardownPage.foundingCohortChip")}
              size="small"
              sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}
            />
            <Link component={RouterLink} to="/early-career" color="primary" underline="hover" variant="body2" fontWeight={500}>
              {t("TeardownPage.seeProgramLink")}
            </Link>
          </Stack>
        </Paper>

        {/* Footer */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 5 }}>
          {t("TeardownPage.footerText")}{" "}
          <Link component={RouterLink} to="/refunds" color="primary" underline="hover">{t("TeardownPage.refundPolicyLink")}</Link>{" "}
          ·{" "}
          <Link component={RouterLink} to="/privacy" color="primary" underline="hover">{t("TeardownPage.privacyLink")}</Link>
        </Typography>
      </Container>
    </Box>
  );
}
